import { useEffect } from 'react';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { Box, Button, Card, FormLayout, InlineStack, RadioButton, Text, TextField, BlockStack } from '@shopify/polaris';
import { useTransferEntry, useTransferHeader } from '#client/stores/transfer/useTransferStore';
import TRANSFER_STRINGS from '#client/pages/Transfers/constants/transfers.en.json';

export default function TransferItemEntry(props) {
    const { addToast } = useToast();
    const { status } = useTransferHeader();
    const { qtyValue, skuValue, setQtyValue, setSkuValue, barcodeValue, setBarcodeValue, handleManualSubmit, handleScannedSubmit, fieldErrors, entryMode, setEntryMode, mode } =
        useTransferEntry(props);

    let timerRef = { current: 0 };
    let scannedRef = { current: '' };

    useEffect(() => {
        const handleKeyPress = async (event) => {
            // Collect characters from barcode scanner

            if (typeof event.key === 'string' && event.key.length === 1) {
                scannedRef.current += event.key;

                // Reset buffer timer on each keypress
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    scannedRef.current = '';
                }, 300);
            }

            // Process complete barcode when Enter is pressed
            if (event.key === 'Enter' && entryMode === 'scan-mode' && scannedRef.current) {
                const barcodeValue = scannedRef.current.trim();
                scannedRef.current = '';

                try {
                    await handleScannedSubmit(addToast, barcodeValue);
                } catch (error) {
                    // Set appropriate error message based on mode
                    const errorMessage = TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR;

                    addToast({
                        type: 'error',
                        message: errorMessage,
                    });
                } finally {
                    //setLoading(false);
                }
            }
        };

        // Add event listener for barcode scanner
        window.addEventListener('keydown', handleKeyPress);

        // Cleanup function
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [entryMode, handleScannedSubmit]);

    return (
        <>
            {status !== 'Complete' && (
                <Card>
                    <Text variant="headingLg">Item Entry</Text>
                    <Box style={{ paddingTop: '1rem' }}>
                        <InlineStack gap="200">
                            <RadioButton
                                label={TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.SCAN_MODE}
                                id="scan-mode"
                                name="entry-mode"
                                checked={entryMode === 'scan-mode'}
                                onChange={() => {
                                    setEntryMode('scan-mode');
                                }}
                            />
                            <RadioButton
                                label={TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.MANUAL_MODE}
                                id="manual-entry"
                                name="entry-mode"
                                checked={entryMode === 'manual-entry'}
                                onChange={() => {
                                    setEntryMode('manual-entry');
                                }}
                            />
                        </InlineStack>
                        {entryMode === 'scan-mode' ? (
                            <>
                                <BlockStack>
                                    <div style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
                                        <Text variant="bodyLg" as="p" alignment="center"></Text>
                                    </div>
                                </BlockStack>
                            </>
                        ) : (
                            <FormLayout>
                                <Box style={{ paddingTop: '.5rem' }}>
                                    <FormLayout.Group>
                                        {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                        <div
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    handleManualSubmit(addToast);
                                                }
                                            }}
                                        >
                                            <TextField
                                                label={TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.PRODUCT_SKU}
                                                value={skuValue}
                                                onChange={setSkuValue}
                                                autoComplete="off"
                                                error={fieldErrors?.sku}
                                            />
                                        </div>

                                        {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                        <div
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    handleManualSubmit(addToast);
                                                }
                                            }}
                                        >
                                            <TextField
                                                label={TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.PRODUCT_BARCODE}
                                                value={barcodeValue}
                                                onChange={setBarcodeValue}
                                                autoComplete="off"
                                                onEnterPressed={() => {
                                                    handleManualSubmit(addToast);
                                                }}
                                                error={fieldErrors?.barcode}
                                            />
                                        </div>
                                    </FormLayout.Group>
                                </Box>
                                <FormLayout.Group>
                                    <Box style={{ paddingTop: '.5rem' }}>
                                        {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                        <div
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    handleManualSubmit(addToast);
                                                }
                                            }}
                                        >
                                            <TextField label={TRANSFER_STRINGS.QUANTITY} value={qtyValue} onChange={setQtyValue} autoComplete="off" error={fieldErrors?.quantity} />
                                        </div>
                                    </Box>
                                </FormLayout.Group>
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            handleManualSubmit(addToast);
                                        }}
                                    >
                                        {mode == 'receive' ? 'Receive Item' : 'Add Item'}
                                    </Button>
                                </InlineStack>
                            </FormLayout>
                        )}
                    </Box>
                </Card>
            )}
        </>
    );
}
